import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { shellStyles } from '../../app/AppShell'
import { AccordionItem } from '../../ui/Accordion'
import { Button } from '../../ui/Button'
import { EmptyState } from '../../ui/Feedback'
import { HELP_INTRO, HELP_SECTIONS, type HelpBlock } from './content'
import { HelpSearchField } from './HelpSearchField'
import { buildIndex, filterSections, pieces, snippet, tokenize, type IndexedSection } from './search'
import styles from './Help.module.css'

/**
 * The Help Centre: what DealPulse's words mean.
 *
 * No endpoints, no loading state, no error state — the content ships with the
 * app (docs/16 section 5.8). It reads from `content.ts`, which is the single
 * place this text exists, and which builds its signal, stage and recommendation
 * names from the same dictionary the pipeline cards use.
 *
 * Sections are collapsed by default. People arrive here with one question, so
 * the list of titles is the useful thing on first paint; an unrolled wall of
 * prose would bury the very heading they came for.
 *
 * The filter is what makes that list navigable at nineteen sections. It matches
 * on the content rather than the rendered page, because a closed panel is not in
 * the DOM at all, so a section stays findable before anyone opens it.
 */
export function HelpPage() {
  const [query, setQuery] = useState('')
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set())

  const index = useMemo(() => buildIndex(HELP_SECTIONS), [])
  const tokens = useMemo(() => tokenize(query), [query])
  const hits = useMemo(() => filterSections(index, tokens), [index, tokens])

  // A single survivor leaves nothing to choose between, so it opens itself —
  // once, when it becomes the only one. After that it behaves like any other
  // section, which is what makes closing it actually close it.
  const only = tokens.length && hits.length === 1 ? (hits[0]?.section.id ?? null) : null
  const lastOnly = useRef<string | null>(null)
  useEffect(() => {
    if (only && only !== lastOnly.current) setOpenIds((ids) => new Set(ids).add(only))
    lastOnly.current = only
  }, [only])

  const toggle = (id: string) =>
    setOpenIds((ids) => {
      const next = new Set(ids)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <>
      <header className={shellStyles.topbar}>
        <h1 className={styles.pageTitle}>Help Centre</h1>
      </header>

      <main className={shellStyles.content}>
        <div className={styles.page}>
          <p className={styles.lead}>{HELP_INTRO}</p>

          <div>
            <HelpSearchField query={query} onQuery={setQuery} count={hits.length} total={index.length} />

            <div className={styles.sections} id="help-sections">
              {hits.map((entry) => (
                <Section
                  key={entry.section.id}
                  entry={entry}
                  tokens={tokens}
                  open={openIds.has(entry.section.id)}
                  onToggle={() => toggle(entry.section.id)}
                />
              ))}

              {hits.length === 0 ? (
                <EmptyState
                  title={`Nothing in Help matches “${query}”`}
                  body="Try a shorter word, or a term you saw on a deal. If the answer is not here at all, tell us with Send feedback."
                  action={
                    <Button onClick={() => setQuery('')}>
                      Clear search
                    </Button>
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

function Section({
  entry,
  tokens,
  open,
  onToggle,
}: {
  entry: IndexedSection
  tokens: string[]
  open: boolean
  onToggle: () => void
}) {
  const context = tokens.length && !open ? snippet(entry, tokens) : ''
  return (
    <AccordionItem
      title={<Rich body={entry.section.title} tokens={tokens} />}
      caption={context ? <Rich body={context} tokens={tokens} /> : undefined}
      open={open}
      onToggle={onToggle}
    >
      {entry.section.blocks.map((block, i) => (
        <Block key={i} block={block} tokens={tokens} />
      ))}
    </AccordionItem>
  )
}

function Block({ block, tokens }: { block: HelpBlock; tokens: string[] }) {
  if (block.kind === 'note') {
    return (
      <p className={styles.note}>
        <Rich body={block.body} tokens={tokens} />
      </p>
    )
  }

  if (block.kind === 'list') {
    const List = block.ordered ? 'ol' : 'ul'
    return (
      <List className={block.ordered ? styles.ordered : styles.list}>
        {block.items.map((item, i) => (
          <li key={i}>
            {item.term ? (
              <strong>
                <Rich body={item.term} tokens={tokens} />
              </strong>
            ) : null}
            {item.term ? ' — ' : null}
            <Rich body={item.body} tokens={tokens} />
          </li>
        ))}
      </List>
    )
  }

  return (
    <p className={styles.paragraph}>
      <Rich body={block.body} tokens={tokens} />
    </p>
  )
}

/** The content's only markup, **bold**, plus the query, without innerHTML. */
function Rich({ body, tokens }: { body: string; tokens: string[] }) {
  return (
    <>
      {pieces(body, tokens).map((piece, i) => {
        const text = piece.mark ? <mark className={styles.mark}>{piece.text}</mark> : piece.text
        return <Fragment key={i}>{piece.strong ? <strong>{text}</strong> : text}</Fragment>
      })}
    </>
  )
}
